import { useState } from "react";
import styled from "styled-components/macro";
import { ClickAwayListenerNS } from "pages/DashboardPages/ProcessTilesControls/ClickAwayListenerNS";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsDarkMode } from "store/atoms";
import { darkModeBackgroundHoveredColor } from "constants/colors";
import { TagsGridModal } from "components/Dashboard/TrendChart/TrendChartContent/SupervisorContext/SupervisorTagMinutes/TagsGridModal";
import { HubletTag } from "components/Dashboard/TrendChart/TrendChartContent/SupervisorContext/supervisorContextUtils";
import { Button } from "@mui/material";

/**
 * A select that displays the tag tree in a multilayered dropdown of tags by category
 */
export default function TagTreeSelect({
  tagValue,
  onTagSelect,
  hubletTags,
}: {
  tagValue: string | null;
  onTagSelect: (tag: HubletTag) => void;
  hubletTags?: HubletTag[];
}) {
  const { translate } = useFlightTranslation();
  const [open, setOpen] = useState(false);
  const [pnlCategory, setPnlCategory] = useState(null as string | null);
  const selectLabel = translate(tagValue ?? "Open_Tag_Sheet");
  const loading = !hubletTags;

  const handleOutsideMenuClick = () => {
    setOpen(false);
  };
  const handleTagMenuItemClick = (tag: HubletTag) => {
    onTagSelect(tag);
    setPnlCategory(pnlCategory);
    setOpen(false);
  };
  const [isDarkMode] = useIsDarkMode();
  return (
    <ClickAwayListenerNS onClickAway={handleOutsideMenuClick}>
      <TagTreeCustomSelectStyles
        $isTagSelected={Boolean(tagValue)}
        $isDarkMode={isDarkMode}
      >
        <Button
          variant="outlined"
          color="primary"
          disabled={loading}
          onClick={() => setOpen(!open)}
        >
          {selectLabel}
        </Button>
        {open && hubletTags?.length && hubletTags.length > 0 && (
          <TagsGridModal
            {...{
              handleFinalTagClick: handleTagMenuItemClick,
              isTagsGridOpen: open,
              setIsTagsGridOpen: setOpen,
              hubletTags: hubletTags || [],
            }}
          />
        )}
        {loading && (
          <div className="spinner">
            <Spinner scale={0.2} />
          </div>
        )}
      </TagTreeCustomSelectStyles>
    </ClickAwayListenerNS>
  );
}

const TagTreeCustomSelectStyles = styled.div<{
  $isTagSelected: boolean;
  $isDarkMode: boolean;
}>`
  height: 100%;
  position: relative;
  margin: 8px;
  .select {
    display: flex;
    position: relative;
    text-align: left;
    color: ${props => (props.$isTagSelected ? "black" : "#c5c5c5")};
    box-sizing: border-box;
    padding: 6px 24px 7px 0;
    border-bottom: 1px solid #555555;
    &:hover {
      background-color: ${({ $isDarkMode }) =>
        $isDarkMode ? darkModeBackgroundHoveredColor : "#f5f5f5"};
      border-radius: 4px 4px 0 0;
    }
    &:active {
      border-bottom: 1px solid #4ba3d1;
    }
    .category-circle-small {
      height: 9px;
      width: 9px;
      border-radius: 100%;
      margin: auto 5px auto 0;
    }
    .label {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  .spinner {
    pointer-events: none;
    position: absolute;
    left: 0;
    right: 0;
    -webkit-transform: translateY(-65%);
    transform: translateY(-65%);
  }

  .MuiSvgIcon-root {
    &.select-icon {
      color: #4ba3d1;
      position: absolute;
      right: 0;
      top: calc(50% - 12px);
    }
  }
  .MuiFormLabel-root.Mui-focused {
    color: #c5c5c5;
  }
  .MuiInputLabel-root {
    color: #c5c5c5;
  }
`;
